import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../hooks/use-is-mounted";
import AutocompleteBase from "../autocomplete-base";
import {AutocompleteProps, AutocompleteRenderOptionState, TextFieldProps} from "@mui/material";
import {CancelToken} from "axios";
import Api from "../../../../../core/services/api/api";

/**
 * Interface representing a Warehouse option in the autocomplete dropdown.
 * This structure matches the API response format for Warehouse data.
 *
 * @property id - Unique identifier for the Warehouse
 * @property name - Display name of the Warehouse
 * @property city - City where the Warehouse is located
 */
interface WarehouseOption {
    id: number;
    code?: string;
    name?: string;
}

/**
 * Props interface for the WarehouseAutocomplete component.
 * Extends the base autocomplete functionality with Warehouse-specific features.
 *
 * @property textFieldProps - Additional props to pass to the TextField component
 * @property form - Whether the component is used within a form
 * @property name - Name attribute for form integration
 * @property required - Whether the field is required in forms
 * @property setLoadingOptions - Callback to handle loading state changes
 * @property value - Currently selected Warehouse option
 * @property onChange - Callback fired when selection changes
 * @property renderOption - Custom render function for dropdown options
 */
interface WarehouseAutocompleteProps<M extends boolean = false> extends Omit<AutocompleteProps<WarehouseOption, M, false, false>, 'renderInput' | 'renderOption' | 'options'> {
    textFieldProps?: Partial<TextFieldProps>;
    form?: boolean;
    name?: string;
    required?: boolean;
    setLoadingOptions?: (loading: boolean) => void;
    renderOption?: (
        props: React.HTMLAttributes<HTMLLIElement>,
        option: WarehouseOption,
        state: AutocompleteRenderOptionState,
        defaultRender: (props: React.HTMLAttributes<HTMLLIElement>, option: WarehouseOption) => React.ReactNode
    ) => React.ReactNode;

    [key: string]: any;
}

/**
 * Interface for the API response when searching warehouses.
 * @property data - Array of Warehouse options returned from the search
 */
interface WarehousesSearchResponse {
    data: WarehouseOption[];
}

/**
 * WarehouseAutocomplete Component
 *
 * A searchable dropdown component for selecting warehouses with the following features:
 * - Asynchronous search with debouncing
 * - Custom option rendering with name and city display
 * - Loading state management
 * - Form integration support
 * - Customizable styling and behavior
 *
 * @component
 */
const WarehouseAutocomplete = <M extends boolean = false, >({
                                                                textFieldProps = {},
                                                                form = false,
                                                                name = undefined,
                                                                required = false,
                                                                setLoadingOptions: setLoadingOptionsProps,
                                                                renderOption: customRenderOption,
                                                                ...props
                                                            }: WarehouseAutocompleteProps<M>) => {
    const isMounted = useIsMounted();
    const [loadingOptions, setLoadingOptions] = useState(false);

    /**
     * Syncs the internal loading state with the parent component
     */
    useEffect(() => {
        setLoadingOptionsProps?.(loadingOptions);
    }, [loadingOptions, setLoadingOptionsProps]);

    /**
     * Fetches Warehouse options based on search keywords
     * Handles loading state and component unmounting
     *
     * @param cancelToken - Axios cancel token for request cancellation
     * @param keywords - Search query string
     * @returns Array of matching Warehouse options
     */
    const getOptions = useCallback(async (cancelToken: CancelToken) => {
        setLoadingOptions(true);
        try {
            // @ts-expect-error Api method exists but is not yet typed
            const response = (await Api.getOperatorWarehouses(cancelToken)) as WarehousesSearchResponse;
            if (!isMounted())
                return [];
            return response?.data ?? [];
        } finally {
            if (isMounted()) {
                setLoadingOptions(false);
            }
        }
    }, [isMounted]);

    /**
     * Determines if two Warehouse options are equal based on their IDs
     */
    const isOptionEqualToValue = useCallback((option: WarehouseOption, value: WarehouseOption | null) =>
        option.id === value?.id || (option.code === value?.code && option.code != null), []);

    /**
     * Extracts the display label from a Warehouse option
     */
    const getOptionLabel = useCallback((option: WarehouseOption) => `${option?.name ?? ""} ${option?.code ? `(${option.code})` : ""}`, []);

    /**
     * Gets the unique key for a Warehouse option
     */
    const getOptionKey = useCallback((option: WarehouseOption) => option?.id ?? ((option.code != null) ? `code_${option.code}` : null) ?? "", []);

    // GetOptions will populate an initial empty array
    const defaultOptions = useMemo(() => [], []);

    return (
        <AutocompleteBase<WarehouseOption, M>
            canSearchEmptyKeywords
            keywordsBased={false}
            getOptions={getOptions}
            timeout={1000}
            textFieldProps={textFieldProps}
            form={form}
            name={name}
            required={required}
            isOptionEqualToValue={isOptionEqualToValue}
            getOptionLabel={getOptionLabel}
            getOptionKey={getOptionKey}
            options={defaultOptions}
            clearOnBlur={false}
            {...props}
        />
    );
};

export default WarehouseAutocomplete;